import React, { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTheme } from "react-jss";
import { IGenericPayload } from "../../../../../../../models";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux/hooks";
import { compileNameOfProperty, getGenericPayloadFromEvent, mapIDescriptionToDropDownOptions } from "../../../../../../../utils";
import { MasterMerchandiseCategoryFormStyle } from "./formStyle.jss";
import { Selector } from "../../../../../../common/selectors/selector";
import { FormField } from "../../../../../../common/fields/fields";
import { NotDataWarningMessage } from "../../../../notDataWarning/notDataWarning";

import { DefaultValues } from "../../../../../../../common/enum/DefaultValues";
import { PopUpCodes } from "../../../../../../../common/enum/PopUpCodes";
import { IMerchandiseCategoryGroup } from "../../../../../../../models/resources/master/IGroupMerchandiseCategory";
import { updateMasterGroupMerchandiseCategoryProperty, setMasterGroupMerchandiseCategoryForm } from "../../../../../../../redux/reducers/resources/master/groupMerchandiseCategory/form";
interface IMasterGroupMerchandiseCategoryFormProps {
    selectorData: IMerchandiseCategoryGroup[];
    showNotData: boolean;
}

export const MasterGroupMerchandiseCategoryForm: React.FC<IMasterGroupMerchandiseCategoryFormProps> = ({ selectorData, showNotData }) => {
    const { t } = useTranslation("common", { keyPrefix: "MASTERS" });
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const style = MasterMerchandiseCategoryFormStyle({ theme });
    const { form } = useAppSelector((state) => state.masterGroupMerchandiseCategoryForm);
    const { showPopUp } = useAppSelector((state) => state.masterPopUp);

    const { register } = useForm<IMerchandiseCategoryGroup>({ mode: "onChange" });

    const onUpdateFormProperty = (name: string, value: any) => {
        const payload: IGenericPayload = {
            name,
            value,
        };

        dispatch(updateMasterGroupMerchandiseCategoryProperty(payload));
    };

    const onInputChange = useCallback(
        (event: any) => {
            const { name, value } = getGenericPayloadFromEvent(event);
            onUpdateFormProperty(name, value);
        },
        [dispatch],
    );

    const onSelectorChange = useCallback(
        (event: any, option: any, name: string) => {
            onUpdateFormProperty(name, option.key);
            const merchandiseCategory = selectorData.find((item) => {
                return item.id === option.key;
            });
            if (merchandiseCategory) {
                let newForm: IMerchandiseCategoryGroup = {
                    active: true,
                    code: merchandiseCategory.code,
                    description: merchandiseCategory.description,
                    referenceId: String(option.key ?? merchandiseCategory.id),
                    id: 0,
                };
                dispatch(setMasterGroupMerchandiseCategoryForm(newForm));
            }
        },
        [dispatch, selectorData],
    );

    const disabledInput = useMemo(() => {
        return showPopUp === PopUpCodes.CREATE && !form.referenceId;
    }, [showPopUp, form]);

    return showNotData ? (
        <NotDataWarningMessage></NotDataWarningMessage>
    ) : (
        <div className={style.form}>
            {showPopUp === PopUpCodes.CREATE && (
                <Selector
                    name={compileNameOfProperty<IMerchandiseCategoryGroup>("referenceId")}
                    title={t("FORM.MERCHANDISE_CATEGORY_GROUP")}
                    options={mapIDescriptionToDropDownOptions(selectorData)}
                    onChange={onSelectorChange}
                    selectedKey={Number(form.referenceId)}
                    required={true}
                />
            )}

            <div className={style.rowForm}>
                <FormField
                    type="text"
                    label={t("CODE")}
                    value={form.code ?? DefaultValues.NOT_TEXT}
                    disabled={true}
                    {...register(compileNameOfProperty<IMerchandiseCategoryGroup>("code"), {
                        onChange: onInputChange,
                        value: form.code ?? DefaultValues.NOT_TEXT,
                    })}
                    isRequired={true}
                />

                <FormField
                    type="text"
                    label={t("DESCRIPTION")}
                    value={form.description ?? DefaultValues.NOT_TEXT}
                    {...register(compileNameOfProperty<IMerchandiseCategoryGroup>("description"), {
                        onChange: onInputChange,
                        value: form.description ?? DefaultValues.NOT_TEXT,
                    })}
                    disabled={disabledInput}
                    isRequired={true}
                />
            </div>
        </div>
    );
};
